import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";

const initialState = {
    items: [],
    fetchStatus: "success",
    fetchByIdStatus: "idle",
    createStatus: "idle",
    updateStatus: "idle",
    deleteStatus: "idle",
    manageOwnerStatus: "idle",
    manageUserStatus: "idle",
    pagination: {},
    error: null
};

export const fetchBranches = createAsyncThunk('branch/fetchBranches', async ({page = 1, limit = 10} = {}) => {
    try {
        const response = await api.get(`/sdg/branch?page=${page}&limit=${limit}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const createBranch = createAsyncThunk('branch/createBranch', async (data) => {
    try {
        const response = await api.post(`/sdg/branch`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const deleteBranch = createAsyncThunk('branch/deleteBranch', async (id) => {
    try {
        const response = await api.delete(`/sdg/branch/${id}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const updateBranch = createAsyncThunk('branch/updateBranch', async (data) => {
    try {
        const response = await api.patch(`/sdg/branch/${data.id}`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchBranchById = createAsyncThunk('branch/fetchBranchById', async (id) => {
    try {
        const response = await api.get(`/sdg/branch/${id}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});
export const manageBranchOwner = createAsyncThunk('branch/manageBranchOwner', async (data) => {
    try {
        const response = await api.post(`/sdg/branch/manageowner`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});
export const manageBranchUser = createAsyncThunk('branch/manageBranchUser', async (data) => {
    try {
        const response = await api.post(`/sdg/branch/manageuser`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});


const branchSlice = createSlice({
    name: "branch",
    initialState,
    reducers: {
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
        resetManageOwnerStatus(state) {
            state.manageOwnerStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBranches.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchBranches.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchBranches.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(fetchBranchById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchBranchById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchBranchById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(createBranch.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createBranch.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                const responseData = action.payload?.data?.data;
                const branchType = responseData?.branchType ?? '';
                const _id = responseData?._id;
                state.createdBranch = {branchType, _id};
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createBranch.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })


            .addCase(manageBranchOwner.pending, (state) => {
                state.manageOwnerStatus = 'loading';
            })
            .addCase(manageBranchOwner.fulfilled, (state, action) => {
                const {manageOwnerStatus, error} = handleResponseStatus(action.payload, 'manageOwnerStatus');
                state.manageOwnerStatus = manageOwnerStatus;
                state.error = error;
            })
            .addCase(manageBranchOwner.rejected, (state, action) => {
                state.manageOwnerStatus = 'failed';
                state.error = action.error.message;
            })

            .addCase(manageBranchUser.pending, (state) => {
                state.manageUserStatus = 'loading';
            })
            .addCase(manageBranchUser.fulfilled, (state, action) => {
                const {manageUserStatus, error} = handleResponseStatus(action.payload, 'manageUserStatus');
                state.manageUserStatus = manageUserStatus;
                state.error = error;
            })
            .addCase(manageBranchUser.rejected, (state, action) => {
                state.manageUserStatus = 'failed';
                state.error = action.error.message;
            })


            .addCase(updateBranch.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateBranch.fulfilled, (state, action) => {
                const {updateStatus, error} = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateBranch.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(deleteBranch.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deleteBranch.fulfilled, (state, action) => {
                const {deleteStatus, error} = handleResponseStatus(action.payload, 'deleteStatus');
                state.deleteStatus = deleteStatus;
                state.items = state.items.filter((item) => item.id !== action.payload.data.data._id);
                state.error = error;
            })
            .addCase(deleteBranch.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            });
    }
});

export const {
    resetUpdateStatus,
    resetDeleteStatus,
    resetManageOwnerStatus,
    resetCreateStatus,
} = branchSlice.actions;
export default branchSlice.reducer;
